import { useEffect, useRef, type MouseEvent } from 'react';

export function useMouseDragScroll() {
  const ref = useRef<HTMLDivElement>(null);
  const cleanup = useRef<(() => void) | null>(null);
  const suppressClick = useRef(false);

  useEffect(() => {
    const strip = ref.current;
    if (!strip) return;
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX) || strip.scrollWidth <= strip.clientWidth) return;
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? strip.clientWidth : 1;
      const max = strip.scrollWidth - strip.clientWidth;
      const next = Math.max(0, Math.min(max, strip.scrollLeft + event.deltaY * unit));
      if (next === strip.scrollLeft) return;
      strip.scrollLeft = next;
      event.preventDefault();
    };
    strip.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      strip.removeEventListener('wheel', onWheel);
      cleanup.current?.();
    };
  }, []);

  const onMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    const strip = ref.current;
    if (event.button !== 0 || !strip || strip.scrollWidth <= strip.clientWidth) return;

    cleanup.current?.();
    const startX = event.clientX;
    const startScroll = strip.scrollLeft;
    let dragged = false;

    const onMove = (move: globalThis.MouseEvent) => {
      const distance = move.clientX - startX;
      if (!dragged && Math.abs(distance) < 5) return;
      dragged = true;
      strip.classList.add('is-dragging');
      strip.scrollLeft = startScroll - distance;
      move.preventDefault();
    };
    const onUp = () => {
      cleanup.current?.();
      suppressClick.current = dragged;
      if (dragged) window.setTimeout(() => { suppressClick.current = false; }, 0);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp, { once: true });
    cleanup.current = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      strip.classList.remove('is-dragging');
      cleanup.current = null;
    };
  };

  const onClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!suppressClick.current) return;
    suppressClick.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  return {
    ref,
    onMouseDown,
    onClickCapture,
  };
}
